/**
 * TanStack Query, persisted to IndexedDB.
 *
 * Firestore's own cache already survives a reload, so why persist the query
 * cache too? Because Firestore's cache answers *queries*, and what the app draws
 * is derived — a growth curve, an age in days, a "due today" list. Persisting
 * the query cache is what lets the app paint a full screen on a cold start with
 * the radio off, before Firestore has even finished opening its database.
 *
 * This is also the reason nothing unserialisable may reach the cache. Everything
 * in here is written through `structuredClone` into IndexedDB and read back;
 * a `Timestamp` goes in as an object with methods and comes back without them.
 * See platform/src/converters.ts.
 */
import { QueryClient } from '@tanstack/react-query';
import type { Persister } from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Firestore's listeners push changes; a refetch on window focus would
       * re-run a query the SDK already knows the answer to and bill a read for
       * it. The subscription is the freshness mechanism, not the poll.
       */
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      gcTime: 1000 * 60 * 60 * 24 * 14,
      retry: (failureCount, error) =>
        // Offline is not a failure to retry through — Firestore queues the work
        // and the listener fires when it lands. Retrying only burns battery.
        navigator.onLine && failureCount < 2 && !String(error).includes('permission-denied'),
    },
  },
});

/**
 * `localStorage` would be simpler and is what most examples use. It is also
 * synchronous, capped near 5 MB, and shared with everything else on the origin —
 * a litter with three months of daily weights and events would evict the app's
 * own auth state to make room. IndexedDB has neither problem.
 */
export const persister: Persister = {
  persistClient: async (client) => set('puppy-doctor:query-cache', client),
  restoreClient: async () => get('puppy-doctor:query-cache'),
  removeClient: async () => del('puppy-doctor:query-cache'),
};

/**
 * Bump when a cached shape changes incompatibly. The persisted cache is thrown
 * away rather than parsed, which is the only safe move: a user who has not
 * opened the app for a month has last month's shapes in IndexedDB, and the
 * schemas in platform/ will reject them one component at a time.
 */
// v3: litters gained `status` and `expectedCount`; offspring gained `sequence`
// and a `photoPaths` album, and `markerColor` stopped being nullable. A v2 cache
// fails the schema one component at a time, which is exactly the failure this
// exists to prevent.
// v4: litters gained a `photoPaths` album of their own. A v3 cache had a
// `litter` with no `photoPaths` key at all, so the section drawing it read
// `.length` off `undefined` instead of failing schema validation.
//
// Nothing to bump for the removal of those albums, and that is the rule rather
// than luck: a cached `litter` still carrying `photoPaths` is a document with
// one key nobody asks for, which Zod drops. A FIELD GOING AWAY IS BACKWARD
// COMPATIBLE; a field arriving is not.
export const CACHE_BUSTER = 'v5';
